<script lang="ts" setup>
import type { AdminBreadcrumb, AdminNav, AdminNavItem } from '~kui/types/adminNav';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '~ds/components/ui/breadcrumb';
import { Icon } from '~ds/components/ui/icon';
import { PageContainer } from '~ds/components/ui/page-container';
import { Separator } from '~ds/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '~ds/components/ui/sidebar';

import KUIContentPageShellSection from '../PageShellSection/KUIContentPageShellSection.vue';

interface Props {
  nav: AdminNav;
  activeRouteName: string;
  /**
   * The current path. Every route at or below a nav item's `to` resolves to that item, so a
   * nested route (a detail page, a create form, a sub-tab) inherits its section without having
   * to be listed anywhere. Omit it and only `activeRoutes` matches.
   */
  activeRoutePath?: string;
  headerLabel?: string;
  isContainer?: boolean;
  rootTestId?: string;
  /**
   * Appended to the nav-derived trail — a detail page naming the record it is showing. The nav
   * item then becomes a link back to its index, since it is no longer the last crumb.
   */
  extraCrumbs?: AdminBreadcrumb[];
}

const props = withDefaults(defineProps<Props>(), {
  activeRoutePath: '',
  headerLabel: 'Admin',
  isContainer: true,
  rootTestId: undefined,
  extraCrumbs: () => [],
});

/** A nav item plus the group (and sub-group) labels above it — the trail's ancestors. */
interface NavLocation {
  item: AdminNavItem;
  ancestors: string[];
}

const navLocations = computed<NavLocation[]>(() => {
  const locations: NavLocation[] = props.nav.topItems.map(item => ({ item, ancestors: [] }));

  for (const group of props.nav.groups) {
    for (const subGroup of group.subGroups ?? []) {
      locations.push(...subGroup.items.map(item => ({ item, ancestors: [group.label, subGroup.label] })));
    }
    locations.push(...(group.items ?? []).map(item => ({ item, ancestors: [group.label] })));
  }

  return locations;
});

// Segment-aware so `/content/articles` owns `/content/articles/42` but not `/content/articles-archive`.
const isUnder = (path: string, base: string): boolean => path === base || path.startsWith(`${base}/`);

/**
 * The one nav item the current route belongs to. `activeRoutes` wins where a page sits under a
 * different item than its URL suggests; otherwise the deepest `to` the path falls under claims it,
 * so a nav item nested inside another's path still beats its parent.
 */
const activeLocation = computed<NavLocation | null>(() => {
  const named = navLocations.value.find(({ item }) => item.activeRoutes?.includes(props.activeRouteName));
  if (named) {
    return named;
  }

  const path = props.activeRoutePath.replace(/\/$/, '');
  if (!path) {
    return null;
  }

  return navLocations.value
    .filter(({ item }) => !!item.to && (item.claimsDescendants === false ? path === item.to : isUnder(path, item.to)))
    .sort((a, b) => (b.item.to?.length ?? 0) - (a.item.to?.length ?? 0))[0] ?? null;
});

const isActive = (item: AdminNavItem): boolean => item === activeLocation.value?.item;

const activeClass = 'data-[active=true]:bg-secondary data-[active=true]:text-secondary-foreground data-[active=true]:font-semibold data-[active=true]:hover:bg-secondary';

// A #nav-item-badge sits absolutely at the row's right edge; reserve the space so a
// long label truncates before it reaches the badge (same trick the DS uses for menu actions).
const badgeGutterClass = 'group-has-data-[sidebar=menu-badge]/menu-item:pr-8';

// The location trail for the sticky inset header, derived from the nav tree:
// group → (sub-group) → active item. Ancestors (groups) aren't routes so they
// render as plain text; the active item is where the current page lives. Pages can
// override the whole trail via the #breadcrumb slot, or extend it via `extraCrumbs`.
//
// A route no item claims — one whose record belongs to no single list, so nothing may
// highlight for it — gets no nav trail and no highlight; the page's own `extraCrumbs`
// then stand as the whole trail.
const navTrail = computed<AdminBreadcrumb[]>(() => {
  const location = activeLocation.value;
  if (!location) {
    return [];
  }

  return [
    ...location.ancestors.map(label => ({ label })),
    { label: location.item.label, to: location.item.to },
  ];
});

// A detail page no nav item claims (a record reachable from more than one list, so no single
// item may highlight for it) has no nav trail to extend — its own crumbs are then the whole
// trail, which still names where the record sits and beats showing no location at all.
const breadcrumbTrail = computed<AdminBreadcrumb[]>(() => [...navTrail.value, ...props.extraCrumbs]);
</script>

<template>
  <SidebarProvider
    :data-testid="rootTestId"
  >
    <Sidebar
      collapsible="offcanvas"
      data-testid="admin-sidebar"
      aria-label="Admin"
    >
      <SidebarHeader class="h-14 shrink-0 flex-row items-center border-b border-sidebar-border px-4">
        <span class="font-semibold text-sidebar-foreground">{{ headerLabel }}</span>
      </SidebarHeader>

      <SidebarContent class="px-2 py-2">
        <SidebarGroup
          v-if="nav.topItems.length"
          class="py-0"
        >
          <SidebarMenu>
            <SidebarMenuItem
              v-for="item in nav.topItems"
              :key="item.label"
            >
              <SidebarMenuButton
                as-child
                :is-active="isActive(item)"
                :class="[activeClass, badgeGutterClass]"
              >
                <NuxtLink
                  :to="item.to ?? '#'"
                  :data-testid="item.testId"
                  :aria-current="isActive(item) ? 'page' : undefined"
                >
                  <Icon
                    v-if="item.iconPath"
                    :name="item.iconPath"
                    size="sm"
                  />
                  <span>{{ item.label }}</span>
                </NuxtLink>
              </SidebarMenuButton>
              <slot name="nav-item-badge" :item="item" />
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>

        <KUIContentPageShellSection
          v-for="group in nav.groups"
          :key="group.label"
          :group="group"
          :active-item="activeLocation?.item ?? null"
        >
          <template #nav-item-badge="slotProps">
            <slot name="nav-item-badge" v-bind="slotProps" />
          </template>
        </KUIContentPageShellSection>
      </SidebarContent>

      <SidebarFooter class="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem
            v-for="item in nav.footerItems"
            :key="item.label"
          >
            <SidebarMenuButton
              :data-testid="item.testId"
              @click="item.onClick"
            >
              <Icon
                v-if="item.iconPath"
                :name="item.iconPath"
                size="sm"
              />
              <span>{{ item.label }}</span>
            </SidebarMenuButton>
            <slot name="nav-item-badge" :item="item" />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>

    <SidebarInset class="min-w-0">
      <!-- Sticky inset header (sidebar-02 layout): the sidebar toggle + a
           breadcrumb trail, pinned to the top of the content pane. -->
      <header class="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background px-4">
        <SidebarTrigger
          class="-ml-1"
          data-testid="admin-sidebar-open"
          aria-label="Toggle admin navigation"
        />
        <Separator
          orientation="vertical"
          class="mr-2 data-[orientation=vertical]:h-4"
        />
        <slot name="breadcrumb">
          <Breadcrumb v-if="breadcrumbTrail.length">
            <BreadcrumbList>
              <template
                v-for="(crumb, index) in breadcrumbTrail"
                :key="crumb.label"
              >
                <BreadcrumbItem>
                  <BreadcrumbPage v-if="index === breadcrumbTrail.length - 1">
                    {{ crumb.label }}
                  </BreadcrumbPage>
                  <BreadcrumbLink
                    v-else-if="crumb.to"
                    as-child
                  >
                    <NuxtLink :to="crumb.to">
                      {{ crumb.label }}
                    </NuxtLink>
                  </BreadcrumbLink>
                  <span v-else>{{ crumb.label }}</span>
                </BreadcrumbItem>
                <BreadcrumbSeparator v-if="index < breadcrumbTrail.length - 1" />
              </template>
            </BreadcrumbList>
          </Breadcrumb>
        </slot>
      </header>

      <PageContainer
        :size="isContainer ? 'xl' : 'full'"
        :gutter="isContainer"
        :class="isContainer ? 'py-6' : 'px-6 py-6 lg:px-10'"
      >
        <div
          v-if="$slots.header || $slots['page-action']"
          class="mb-8 flex w-full flex-wrap items-center justify-between gap-4"
        >
          <slot name="header" />
          <div
            v-if="$slots['page-action']"
            class="flex flex-wrap items-center gap-2"
          >
            <slot name="page-action" />
          </div>
        </div>
        <slot />
      </PageContainer>
    </SidebarInset>
  </SidebarProvider>
</template>
